import { Checkbox, FormGroup } from '@carbon/react';
import { CheckboxGroupFilterProps } from '../types';
import styles from './checkbox-group-filter.module.scss';

/**
 * Checkbox-group filter component using Carbon FormGroup.
 * Allows selecting multiple values from a fixed list of options where
 * each option is rendered as a visible checkbox.
 * @param {CheckboxGroupFilterProps} props - Component props
 * @param {string} props.id - Unique identifier for the filter
 * @param {string} [props.label] - Optional group legend; suppressed when omitted
 * @param {string[]} props.value - Array of currently selected values
 * @param {Function} props.onChange - Callback when selection changes
 * @param {Array} props.options - Available options with value/label pairs
 * @return {JSX.Element} The checkbox group filter element
 */
export function CheckboxGroupFilter({
  id,
  label,
  value,
  onChange,
  options,
}: CheckboxGroupFilterProps) {
  const handleToggle = (optionValue: string, checked: boolean) => {
    onChange(
      checked ? [...value, optionValue] : value.filter((v) => v !== optionValue)
    );
  };

  const checkboxes = options.map((option) => (
    <Checkbox
      key={option.value}
      id={`${id}-${option.value}`}
      labelText={option.label}
      checked={value.includes(option.value)}
      onChange={(_evt, { checked }: { checked: boolean }) =>
        handleToggle(option.value, checked)
      }
    />
  ));

  // Skip Carbon's FormGroup wrapper (which always renders a `<legend>`,
  // even with an empty `legendText`) when the caller titles the filter
  // externally — avoids a redundant empty legend with default margin.
  if (!label) {
    return <div className={styles.checkboxGroupFilter}>{checkboxes}</div>;
  }

  return (
    <FormGroup legendText={label} className={styles.checkboxGroupFilter}>
      {checkboxes}
    </FormGroup>
  );
}
